// Core Identity documentation and support links for the IP Anchor pages, derived from the server's
// ExternalLinks configuration (GET /api/config/environment). URLs live in appsettings.json, not here.

/** The ExternalLinks section served by GET /api/config/environment. */
export interface ExternalLinks {
  coreIdentityDocs: string;
  coreIdentityManage: string;
  identityHelp: string;
  reportItNow: string;
  fdpoConsentGuidance: string;
}

const LINK_KEYS: readonly (keyof ExternalLinks)[] = [
  'coreIdentityDocs', 'coreIdentityManage', 'identityHelp', 'reportItNow', 'fdpoConsentGuidance',
];

/** True when every expected link is present as a non-empty string. */
export const isExternalLinks = (value: unknown): value is ExternalLinks =>
  !!value && typeof value === 'object'
  && LINK_KEYS.every((key) => typeof (value as Record<string, unknown>)[key] === 'string'
    && ((value as Record<string, string>)[key]).length > 0);

const trimSlash = (url: string): string => url.replace(/\/+$/, '');

export const coreIdentityLinksFrom = (links: ExternalLinks) => {
  const docs = trimSlash(links.coreIdentityDocs);
  return {
    docsBase: docs,
    owners: `${docs}/npaccounts.html#modify-non-people-account-owners`,
    transfer: `${docs}/npaccounts.html#transfer-ownership-of-your-non-people-accounts`,
    extend: `${docs}/npaccounts.html#extend-your-non-people-accounts`,
    serviceTree: `${docs}/npaccounts.html#npa-service-tree-mapping`,
    faq: `${docs}/npfaq.html`,
    identityHelp: links.identityHelp,
    reportItNow: links.reportItNow,
  } as const;
};

export type CoreIdentityLinks = ReturnType<typeof coreIdentityLinksFrom>;

/** The account's page in Core Identity, with both key segments URI-escaped. */
export const coreIdentityManageUrl = (links: ExternalLinks, domain: string, alias: string): string =>
  `${trimSlash(links.coreIdentityManage)}/${encodeURIComponent(domain)}/${encodeURIComponent(alias)}`;

/**
 * True when a read-only notice should explain that only the account's Core Identity owners can
 * edit: the caller is simply not an owner (no reason), or the reason is about the Core Identity
 * record or owner data. False for disabled, dormant and Frozen accounts and for a missing
 * eligibility verdict, which owner changes do not fix.
 */
export const ownerGuidanceApplies = (reason: string | null): boolean => {
  if (!reason) return true;
  return reason.includes('Core Identity') && !reason.includes('Frozen');
};